import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ArrowLeft,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  RefreshCw,
  Search,
  UserRoundCheck,
  UserRoundPlus,
  Users,
  X,
} from "lucide-react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  checkInExistingVisitor,
  convertVisitorToMember,
  createAndCheckInVisitor,
  getServiceVisitors,
  removeVisitorCheckIn,
} from "@/api/visitors";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { PageContainer } from "@/design-system";
import { AttendanceRoster } from "@/features/attendance/components/AttendanceRoster";
import type { RosterFilter } from "@/features/attendance/components/AttendanceRoster";
import { AttendanceSummaryCard } from "@/features/attendance/components/AttendanceSummaryCard";
import { ConvertVisitorDialog } from "@/features/attendance/components/ConvertVisitorDialog";
import { VisitorCheckInDialog } from "@/features/attendance/components/VisitorCheckInDialog";
import { useAttendanceRoster } from "@/features/attendance/hooks/useAttendance";
import { useCheckIn } from "@/features/attendance/hooks/useCheckIn";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type {
  ConvertVisitorToMemberPayload,
  CreateVisitorPayload,
  Visitor,
  VisitorRosterItem,
} from "@/types/visitor";

export function ServiceAttendancePage() {
  const { churchId } = useAuth();
  const {permissions: churchPermissions} = useEffectivePermissions();
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] =
    useState<RosterFilter>("ALL");

  const [loadingMemberId, setLoadingMemberId] =
    useState<string | null>(null);

  const [
    visitorDialogOpen,
    setVisitorDialogOpen,
  ] = useState(false);

  const [visitorError, setVisitorError] =
    useState<string | null>(null);

  const [loadingVisitorId, setLoadingVisitorId] =
    useState<string | null>(null);

  const [
    selectedVisitor,
    setSelectedVisitor,
  ] = useState<Visitor | null>(null);

  const [
    convertDialogOpen,
    setConvertDialogOpen,
  ] = useState(false);

  const [conversionError, setConversionError] =
    useState<string | null>(null);

  const rosterQuery = useAttendanceRoster(
    churchId,
    serviceId
  );

  const { permissions } = useEffectivePermissions(rosterQuery.data?.ministry_id, !!rosterQuery.data);
  const canViewVisitors = permissions.has("VISITORS_VIEW");
  const canManageVisitors = !!rosterQuery.data?.can_manage_attendance && canViewVisitors && permissions.has("VISITORS_MANAGE") && permissions.has("ATTENDANCE_MANAGE");
  const checkInMutation = useCheckIn(
    churchId,
    serviceId
  );

  const visitorsQuery = useQuery({
    queryKey: [
      "service-visitors",
      churchId,
      serviceId,
    ],
    enabled: !!churchId && !!serviceId && canViewVisitors,
    queryFn: () =>
      getServiceVisitors(
        churchId!,
        serviceId!
      ),
  });

  const createVisitorMutation = useMutation({
    mutationFn: (
      payload: CreateVisitorPayload
    ) => {
      if (!churchId || !serviceId) {
        throw new Error(
          "Church or service occurrence is unavailable."
        );
      }

      return createAndCheckInVisitor(
        churchId,
        serviceId,
        payload
      );
    },

    onSuccess: async () => {
      setVisitorError(null);
      setVisitorDialogOpen(false);

      await refreshVisitorData();
    },

    onError: (error) => {
      setVisitorError(
        getErrorMessage(
          error,
          "Unable to create and check in visitor."
        )
      );
    },
  });

  const checkInVisitorMutation = useMutation({
    mutationFn: (visitorId: string) => {
      if (!churchId || !serviceId) {
        throw new Error(
          "Church or service occurrence is unavailable."
        );
      }

      return checkInExistingVisitor(
        churchId,
        serviceId,
        visitorId
      );
    },

    onSuccess: async () => {
      setVisitorError(null);
      setVisitorDialogOpen(false);

      await refreshVisitorData();
    },

    onError: (error) => {
      setVisitorError(
        getErrorMessage(
          error,
          "Unable to check in visitor."
        )
      );
    },
  });

  const removeVisitorMutation = useMutation({
    mutationFn: (visitorId: string) => {
      if (!churchId || !serviceId) {
        throw new Error(
          "Church or service occurrence is unavailable."
        );
      }

      return removeVisitorCheckIn(
        churchId,
        serviceId,
        visitorId
      );
    },

    onSuccess: async () => {
      await refreshVisitorData();
    },
  });

  const convertVisitorMutation = useMutation({
    mutationFn: ({
      visitorId,
      payload,
    }: {
      visitorId: string;
      payload: ConvertVisitorToMemberPayload;
    }) => {
      if (!churchId) {
        throw new Error(
          "Church is unavailable."
        );
      }

      return convertVisitorToMember(
        churchId,
        visitorId,
        payload
      );
    },

    onSuccess: async (conversion) => {
      setConversionError(null);
      setConvertDialogOpen(false);
      setSelectedVisitor(null);

      await Promise.all([
        refreshVisitorData(),

        queryClient.invalidateQueries({
          queryKey: ["members", churchId],
        }),

        queryClient.invalidateQueries({
          queryKey: ["dashboard", churchId],
        }),

        queryClient.invalidateQueries({
          queryKey: ["households", churchId],
        }),
      ]);

      navigate(
        `/members/${conversion.member_id}`
      );
    },

    onError: (error) => {
      setConversionError(
        getErrorMessage(
          error,
          "Unable to convert visitor to member."
        )
      );
    },
  });

  async function refreshVisitorData() {
    await Promise.all([
      ...["youth-roster", "youth-report", "youth-intelligence", "youth-care"].map(key => queryClient.invalidateQueries({queryKey: [key, churchId]})),
      queryClient.invalidateQueries({ queryKey: ["occurrence-attendance", churchId, serviceId] }),
      queryClient.invalidateQueries({ queryKey: ["attendance-roster", churchId, serviceId] }),
      queryClient.invalidateQueries({
        queryKey: [
          "service-visitors",
          churchId,
          serviceId,
        ],
      }),

      queryClient.invalidateQueries({
        queryKey: ["visitor-search"],
      }),

      queryClient.invalidateQueries({
        queryKey: [
          "attendance-records",
          churchId,
        ],
      }),

      queryClient.invalidateQueries({
        queryKey: ["dashboard", churchId],
      }),
    ]);
  }

  async function handleToggleCheckIn(
    memberId: string,
    checkedIn: boolean
  ) {
    setAttendanceError(null);
    setLoadingMemberId(memberId);

    try {
      await checkInMutation.mutateAsync({
        memberId,
        checkedIn,
      });
    } catch (error) {
      setAttendanceError(getErrorMessage(error, "Attendance could not be saved. Refresh the roster and check eligibility."));
    } finally {
      setLoadingMemberId(null);
    }
  }

  async function handleCheckInVisitor(
    visitorId: string
  ) {
    setVisitorError(null);
    setLoadingVisitorId(visitorId);

    try {
      await checkInVisitorMutation.mutateAsync(
        visitorId
      );
    } finally {
      setLoadingVisitorId(null);
    }
  }

  async function handleCreateVisitor(
    payload: CreateVisitorPayload
  ) {
    setVisitorError(null);

    await createVisitorMutation.mutateAsync(
      payload
    );
  }

  async function handleRemoveVisitor(
    visitorId: string
  ) {
    setLoadingVisitorId(visitorId);

    try {
      await removeVisitorMutation.mutateAsync(
        visitorId
      );
    } finally {
      setLoadingVisitorId(null);
    }
  }

  function handleOpenConversion(
    visitor: Visitor
  ) {
    setConversionError(null);
    setSelectedVisitor(visitor);
    setConvertDialogOpen(true);
  }

  async function handleConvertVisitor(
    payload: ConvertVisitorToMemberPayload
  ) {
    if (!selectedVisitor) {
      return;
    }

    setConversionError(null);

    await convertVisitorMutation.mutateAsync({
      visitorId: selectedVisitor.id,
      payload,
    });
  }

  if (
    rosterQuery.isLoading ||
    visitorsQuery.isLoading
  ) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (
    rosterQuery.isError ||
    !rosterQuery.data
  ) {
    return (
      <PageContainer>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-6 text-rose-300">
          Unable to load attendance roster.
        </div>
      </PageContainer>
    );
  }

  const roster = rosterQuery.data;

  const visitors =
    visitorsQuery.data?.visitors ?? [];

  const visitorCount =
    canViewVisitors ? visitorsQuery.data?.visitor_count : undefined;

  const totalAttendance =
    roster.present_count + (visitorCount ?? 0);

  const counts = {
    all: roster.total_members,
    checkedIn: roster.present_count,
    notCheckedIn:
      roster.not_checked_in_count,
  };

  const visitorIsSubmitting =
    createVisitorMutation.isPending ||
    checkInVisitorMutation.isPending;

  return (
    <>
      <PageContainer>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <Link
            to={permissions.has("SERVICES_VIEW") ? `/services/${serviceId}` : roster.ministry_id ? "/youth" : "/attendance"}
            className="flex items-center gap-2 text-sm font-medium text-indigo-400 hover:text-indigo-300"
          >
            <ArrowLeft size={16} />
            Back to overview
          </Link>

          {roster.ministry_id && <Link className="text-indigo-300" to="/youth">Youth Ministry operations</Link>}
          <Link
            to={roster.ministry_id ? "/youth" : "/attendance"}
            className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-indigo-500/40 hover:text-white"
          >
            <BarChart3 size={17} />
            Attendance Overview
          </Link>
        </div>

        <ServiceCheckInHero
          readOnly={!canManageVisitors}
          title={`${roster.ministry_name} — ${roster.service_title}`}
          serviceDate={roster.service_date}
          totalAttendance={totalAttendance}
          visitorCount={visitorCount}
          onAddVisitor={() => {
            setVisitorError(null);
            setVisitorDialogOpen(true);
          }}
        />

        <div className="mt-7">
          <AttendanceSummaryCard
            totalMembers={roster.total_members}
            checkedInCount={roster.present_count}
            notCheckedInCount={
              roster.not_checked_in_count
            }
          />
        </div>

        {visitors.length > 0 && (
          <div className="mt-7">
            {canViewVisitors && <VisitorRosterCard
              visitors={visitors}
              readOnly={!canManageVisitors}
              canConvert={churchPermissions.has("VISITORS_MANAGE") && churchPermissions.has("MEMBERS_MANAGE")}
              loadingVisitorId={loadingVisitorId}
              onRemoveVisitor={
                handleRemoveVisitor
              }
              onConvertVisitor={
                handleOpenConversion
              }
            />}
          </div>
        )}

        {attendanceError && <p role="alert" className="mt-4 rounded border border-rose-500/30 p-3 text-rose-300">{attendanceError}</p>}
        <div className="mt-7 grid items-start gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
          <RosterControls
            readOnly={!canManageVisitors}
            search={search}
            filter={filter}
            counts={counts}
            visitorCount={visitorCount}
            onSearchChange={setSearch}
            onFilterChange={setFilter}
            onAddVisitor={() => {
              setVisitorError(null);
              setVisitorDialogOpen(true);
            }}
            onRefresh={async () => {
              await Promise.all([
                rosterQuery.refetch(),
                ...(canViewVisitors ? [visitorsQuery.refetch()] : []),
              ]);
            }}
            isRefreshing={
              rosterQuery.isFetching ||
              visitorsQuery.isFetching
            }
          />

          <AttendanceRoster
            roster={roster.roster}
            readOnly={!roster.can_manage_attendance}
            loadingMemberId={loadingMemberId}
            search={search}
            filter={filter}
            onToggleCheckIn={
              handleToggleCheckIn
            }
          />
        </div>
      </PageContainer>

      {churchId && serviceId && (
        <VisitorCheckInDialog
          open={visitorDialogOpen}
          churchId={churchId}
          serviceOccurrenceId={serviceId}
          isSubmitting={visitorIsSubmitting}
          errorMessage={visitorError}
          onClose={() => {
            if (!visitorIsSubmitting) {
              setVisitorDialogOpen(false);
              setVisitorError(null);
            }
          }}
          onCheckInExisting={
            handleCheckInVisitor
          }
          onCreateVisitor={
            handleCreateVisitor
          }
        />
      )}

      <ConvertVisitorDialog
        open={convertDialogOpen}
        visitor={selectedVisitor}
        isSubmitting={
          convertVisitorMutation.isPending
        }
        errorMessage={conversionError}
        onClose={() => {
          if (
            !convertVisitorMutation.isPending
          ) {
            setConvertDialogOpen(false);
            setSelectedVisitor(null);
            setConversionError(null);
          }
        }}
        onSubmit={handleConvertVisitor}
      />
    </>
  );
}

function ServiceCheckInHero({
  readOnly,
  title,
  serviceDate,
  totalAttendance,
  visitorCount,
  onAddVisitor,
}: {
  readOnly: boolean;
  title: string;
  serviceDate: string;
  totalAttendance: number;
  visitorCount?: number;
  onAddVisitor: () => void;
}) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-violet-950/25 px-7 py-8 shadow-xl shadow-black/20">
      <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-violet-500/10 blur-3xl" />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-400">
            <CalendarDays size={30} />
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                {title}
              </h1>

              <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                Service roster
              </span>
            </div>

            <p className="mt-3 text-base text-slate-300">
              {new Date(
                `${serviceDate}T12:00:00`
              ).toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>

            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <Users size={15} />
                {totalAttendance} {visitorCount === undefined ? "member check-ins" : "total attendance"}
              </span>

              <span className="flex items-center gap-2">
                <UserRoundPlus size={15} />
                {visitorCount === undefined ? "Visitor information unavailable" : `${visitorCount} visitor${visitorCount === 1 ? "" : "s"}`}
              </span>
            </div>

            <p className="mt-3 text-sm text-slate-500">
              Members and visitors may be checked in or removed at any time.
            </p>
          </div>
        </div>

        <button
          type="button"
          disabled={readOnly}
          onClick={onAddVisitor}
          className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-5 py-3 font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-950/30"
        >
          <UserRoundPlus size={18} />
          Visitor Check-In
        </button>
      </div>
    </section>
  );
}

function VisitorRosterCard({
  readOnly, canConvert,
  visitors,
  loadingVisitorId,
  onRemoveVisitor,
  onConvertVisitor,
}: {
  readOnly: boolean;
  canConvert: boolean;
  visitors: VisitorRosterItem[];
  loadingVisitorId: string | null;

  onRemoveVisitor: (
    visitorId: string
  ) => Promise<void>;

  onConvertVisitor: (
    visitor: Visitor
  ) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-500/15 text-sky-400">
            <UserRoundPlus size={20} />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white">
              Visitors
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Guests checked into this service
            </p>
          </div>
        </div>

        <span className="rounded-full bg-sky-500/15 px-3 py-1 text-xs font-semibold text-sky-300">
          {visitors.length} visitor
          {visitors.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="divide-y divide-slate-800">
        {visitors.map(
          ({ visitor, attendance }) => {
            const isLoading =
              loadingVisitorId === visitor.id;

            const isConverted =
              visitor.status === "CONVERTED" ||
              !!visitor.converted_member_id;

            return (
              <div
                key={visitor.id}
                className="flex flex-col gap-4 px-6 py-5 transition hover:bg-slate-800/30 lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-bold ${
                      isConverted
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-sky-500/15 text-sky-300"
                    }`}
                  >
                    {getInitials(
                      visitor.first_name,
                      visitor.last_name
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-white">
                        {visitor.first_name}{" "}
                        {visitor.last_name}
                      </p>

                      {isConverted ? (
                        <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-300">
                          <UserRoundCheck size={12} />
                          Member
                        </span>
                      ) : (
                        <span className="rounded-full bg-sky-500/10 px-2.5 py-1 text-xs font-semibold text-sky-300">
                          {visitor.visit_count === null ? "Visit history unavailable" : visitor.visit_count === 1
                            ? "First Visit"
                            : `Visit ${visitor.visit_count}`}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      {visitor.phone && (
                        <span>
                          {visitor.phone}
                        </span>
                      )}

                      {visitor.email && (
                        <span>
                          {visitor.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:justify-end">
                  <div className="min-w-[120px]">
                    <p className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
                      <CheckCircle2 size={17} />
                      Checked In
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      {formatTime(
                        attendance.checked_in_at
                      )}
                    </p>
                  </div>

                  {!isConverted && canConvert && (
                    <button
                      type="button"
                      onClick={() =>
                        onConvertVisitor(visitor)
                      }
                      className="min-h-11 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:-translate-y-0.5 hover:bg-emerald-500/10"
                    >
                      Convert to Member
                    </button>
                  )}

                  {isConverted &&
                    visitor.converted_member_id && (
                      <Link
                        to={`/members/${visitor.converted_member_id}`}
                        className="flex min-h-11 items-center justify-center rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-500/10"
                      >
                        View Member
                      </Link>
                    )}

                  <button
                    type="button"
                    disabled={isLoading || readOnly}
                    onClick={() =>
                      onRemoveVisitor(visitor.id)
                    }
                    className="min-h-11 rounded-xl border border-slate-700 bg-slate-950/50 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-rose-500/30 hover:bg-rose-500/5 hover:text-rose-300 disabled:opacity-60"
                  >
                    {isLoading
                      ? "Updating..."
                      : "Undo Check-In"}
                  </button>
                </div>
              </div>
            );
          }
        )}
      </div>
    </Card>
  );
}

function RosterControls({
  readOnly,
  search,
  filter,
  counts,
  visitorCount,
  onSearchChange,
  onFilterChange,
  onAddVisitor,
  onRefresh,
  isRefreshing,
}: {
  readOnly: boolean;
  search: string;
  filter: RosterFilter;

  counts: {
    all: number;
    checkedIn: number;
    notCheckedIn: number;
  };

  visitorCount?: number;

  onSearchChange: (value: string) => void;

  onFilterChange: (
    value: RosterFilter
  ) => void;

  onAddVisitor: () => void;
  onRefresh: () => void;

  isRefreshing: boolean;
}) {
  return (
    <div className="sticky top-24 space-y-5">
      <Card className="p-5">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />

          <input
            value={search}
            onChange={(event) =>
              onSearchChange(event.target.value)
            }
            placeholder="Search name, email, phone, or household..."
            className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950 pl-11 pr-11 text-sm text-white placeholder:text-slate-600 focus:border-violet-500 focus:outline-none"
          />

          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-500 hover:bg-slate-800 hover:text-white"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <FilterButton
            active={filter === "ALL"}
            label="All"
            count={counts.all}
            onClick={() =>
              onFilterChange("ALL")
            }
          />

          <FilterButton
            active={filter === "CHECKED_IN"}
            label="Checked In"
            count={counts.checkedIn}
            onClick={() =>
              onFilterChange("CHECKED_IN")
            }
          />

          <FilterButton
            active={
              filter === "NOT_CHECKED_IN"
            }
            label="Not Checked In"
            count={counts.notCheckedIn}
            onClick={() =>
              onFilterChange(
                "NOT_CHECKED_IN"
              )
            }
          />
        </div>

        <button
          type="button"
          disabled={readOnly}
          onClick={onAddVisitor}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-950/30"
        >
          <UserRoundPlus size={17} />

          Visitor Check-In

          {visitorCount > 0 && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-xs">
              {visitorCount}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-semibold text-slate-300 transition hover:border-violet-500/40 hover:text-white disabled:opacity-60"
        >
          <RefreshCw
            size={16}
            className={
              isRefreshing
                ? "animate-spin"
                : ""
            }
          />

          Refresh Roster
        </button>
      </Card>

      <Card className="p-5">
        <p className="font-semibold text-white">
          Service Check-In
        </p>

        <p className="mt-2 text-sm leading-6 text-slate-400">
          Member and visitor check-ins remain editable. Use Undo Check-In to correct an accidental entry.
        </p>
      </Card>
    </div>
  );
}

function FilterButton({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-2 text-xs font-semibold transition ${
        active
          ? "bg-violet-600 text-white shadow-lg shadow-violet-950/30"
          : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
      }`}
    >
      {label} ({count})
    </button>
  );
}

function getInitials(
  firstName: string,
  lastName: string
) {
  return `${firstName[0] ?? ""}${
    lastName[0] ?? ""
  }`.toUpperCase();
}

function formatTime(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function getErrorMessage(
  error: unknown,
  fallback: string
) {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (
      typeof response?.data?.detail === "string"
    ) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

